import React, { useEffect } from 'react';
import { useAppData } from './context/DataContext';
import { Select, Spin, Space, ConfigProvider } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ReactComponent as Avatar } from "./IconUmmahfy.svg";

const { Option } = Select;

const secrets = require('./secrets');
const api = secrets.server_url;

function OrganizationSelector({ className = '' }) {
  const {
    selectedOrganization,
    selectedOrganizationInfo,
    managedOrganizations,
    setAppData
  } = useAppData();

  const navigate = useNavigate();

  // Define the organization switch handler
  async function handleOrganizationChange(orgName) {
    if (!orgName) return;

    // Handle special case for creating a new organization
    if (orgName === 'create-new') {
      navigate('/create-organization');
      return;
    }

    try {
      // Find the selected organization info
      const orgInfo = managedOrganizations.find(org => org.name === orgName);
      if (!orgInfo) return;

      // Update the app data with the new selected organization
      setAppData(prevData => ({
        ...prevData,
        selectedOrganization: orgName,
        selectedOrganizationInfo: orgInfo
      }));

      // Call the API to update the session
      await axios.post(`${api}switchSelectedOrganization/`, {
        org: orgName
      }, {
        withCredentials: true,
        headers: {
          'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
        }
      });
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  }

  // Use effect to handle auto-selection of the first organization
  useEffect(() => {
    // If no organization is selected but there are organizations, select the first one
    if (!selectedOrganization && managedOrganizations?.length > 0) {
      const firstOrgName = managedOrganizations[0].name;
      handleOrganizationChange(firstOrgName);
    }
  }, [selectedOrganization, managedOrganizations]);

  // Display loading state if no managedOrganizations yet
  if (!managedOrganizations) {
    const loadingContent = (
      <>
        <Spin size="small" /> Loading organizations...
      </>
    );

    if (className.includes('ce-org-selector-grid')) {
      return <div className={className}>{loadingContent}</div>;
    }

    return (
      <div className="organization-selector">
        {loadingContent}
      </div>
    );
  }

  // If there are no organizations, show a message and a button to create one
  if (managedOrganizations.length === 0) {
    const emptyContent = (
      <Space direction="vertical" align="center" style={{ width: '100%' }}>
        <p>You don't have any organizations yet.</p>
        <button
          className="create-org-button"
          onClick={() => navigate('/newCreateEvent')}
        >
          Create an Organization
        </button>
      </Space>
    );

    if (className.includes('ce-org-selector-grid')) {
      return <div className={`${className} empty-state`}>{emptyContent}</div>;
    }

    return (
      <div className="organization-selector empty-state">
        {emptyContent}
      </div>
    );
  }

  // Sort organizations to put the selected one first, if a selection exists
  const sortedOrganizations = [...managedOrganizations].sort((a, b) => {
    if (selectedOrganization && a.name === selectedOrganization) return -1;
    if (selectedOrganization && b.name === selectedOrganization) return 1;
    return 0;
  });

  // If we're in the process of selecting the default organization
  if (!selectedOrganization && sortedOrganizations.length > 0) {
    const defaultContent = (
      <>
        <Spin size="small" /> Setting default organization...
      </>
    );

    if (className.includes('ce-org-selector-grid')) {
      return <div className={className}>{defaultContent}</div>;
    }

    return (
      <div className="organization-selector">
        {defaultContent}
      </div>
    );
  }

  // If className includes grid styling, render without wrapper
  if (className.includes('ce-org-selector-grid')) {
    return (
      <ConfigProvider
        theme={{
          token: {
            colorBgBase: 'rgba(255, 255, 255, 0.075)',
            colorText: '#fff',
            /* here is your global tokens */
          },
        }}
      >

        <Select
          value={selectedOrganization}
          onChange={handleOrganizationChange}
          className={`org-select ${className}`}
          dropdownMatchSelectWidth={true}
          dropdownRender={(menu) => (
            <div className="org-dropdown-container">
              {/* Sticky Create Organization button at top */}
              <div className="create-org-sticky-top">
                <div className="org-option-content create-new-org" onClick={() => handleOrganizationChange('create-new')}>
                  <div className="org-option-icon create-icon">+</div>
                  <span className="org-option-name create-label">Create Organization</span>
                </div>
              </div>

              {/* Scrollable organization list */}
              <div className="org-options-scrollable">
                {sortedOrganizations.map(org => {
                  const optionContent = (
                    <div className="org-option-content">
                      {org.profilePicture ? (
                        <img
                          src={org.profilePicture}
                          alt={org.name}
                          className="org-option-icon"
                        />
                      ) : (
                        <Avatar className="org-option-icon" />
                      )}
                      <span className="org-option-name">
                        {org.name}
                        {org.name === selectedOrganizationInfo?.name &&
                          <span className="default-badge">(Default)</span>
                        }
                      </span>
                    </div>
                  );

                  return (
                    <div key={org._id} className="org-option-item" onClick={() => handleOrganizationChange(org.name)}>
                      {optionContent}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        >
          {sortedOrganizations.map(org => {
            // Pre-render the content for both dropdown and selection display
            const optionContent = (
              <div className="org-option-content">
                {org.profilePicture ? (
                  <img
                    src={org.profilePicture}
                    alt={org.name}
                    className="org-option-icon"
                  />
                ) : (
                  <Avatar className="org-option-icon" />
                )}
                <span className="org-option-name">
                  {org.name}
                  {org.name === selectedOrganizationInfo?.name &&
                    <span className="default-badge">(Default)</span>
                  }
                </span>
              </div>
            );

            return (
              <Option key={org._id} value={org.name} className="mb-3">
                {optionContent}
              </Option>
            );
          })}
        </Select>
      </ConfigProvider>
    );
  }

  // Default render with wrapper for other contexts
  return (
    <div className={`organization-selector ${className}`}>
      <ConfigProvider
        theme={{
          token: {
            colorBgBase: 'rgba(255, 255, 255, 0.075)',
            colorText: '#fff',

            /* here is your global tokens */
          },
        }}
      >
        <Select
          value={selectedOrganization}
          onChange={handleOrganizationChange}
          className="org-select"
          dropdownMatchSelectWidth={true}
          dropdownRender={(menu) => (
            <div className="org-dropdown-container">
              {/* Sticky Create Organization button at top */}
              <div className="create-org-sticky-top">
                <div className="org-option-content create-new-org" onClick={() => handleOrganizationChange('create-new')}>
                  <div className="org-option-icon create-icon">+</div>
                  <span className="org-option-name create-label">Create Organization</span>
                </div>
              </div>

              {/* Scrollable organization list */}
              <div className="org-options-scrollable">
                {sortedOrganizations.map(org => {
                  const optionContent = (
                    <div className="org-option-content">
                      {org.profilePicture ? (
                        <img
                          src={org.profilePicture}
                          alt={org.name}
                          className="org-option-icon"
                        />
                      ) : (
                        <Avatar className="org-option-icon" />
                      )}
                      <span className="org-option-name">
                        {org.name}
                        {org.name === selectedOrganizationInfo?.name &&
                          <span className="default-badge">(Default)</span>
                        }
                      </span>
                    </div>
                  );

                  return (
                    <div key={org._id} className="org-option-item" onClick={() => handleOrganizationChange(org.name)}>
                      {optionContent}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        >
          {sortedOrganizations.map(org => {
            // Pre-render the content for both dropdown and selection display
            const optionContent = (
              <div className="org-option-content">
                {org.profilePicture ? (
                  <img
                    src={org.profilePicture}
                    alt={org.name}
                    className="org-option-icon"
                  />
                ) : (
                  <Avatar className="org-option-icon" />
                )}
                <span className="org-option-name">
                  {org.name}
                  {org.name === selectedOrganizationInfo?.name &&
                    <span className="default-badge">(Default)</span>
                  }
                </span>
              </div>
            );

            return (
              <Option key={org._id} value={org.name} className="mb-3">
                {optionContent}
              </Option>
            );
          })}
        </Select>
      </ConfigProvider>
    </div>
  );
}

export default OrganizationSelector; 